// Single source of truth for PWA install.
//
// Chrome/Android fires `beforeinstallprompt` exactly once, and the event can be
// `.prompt()`-ed only once, so if two components each capture their own copy,
// whichever fires first leaves the other stale. We capture it ONCE here at module
// load and let any number of UI entry points (the bottom banner, the sidebar
// "Get the App" button, a dashboard nudge) share it via subscribe + promptInstall.
let deferred = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(); } catch {} });

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();        // stop Chrome's mini-infobar; we surface our own UI
    deferred = e;
    emit();
  });
  window.addEventListener('appinstalled', () => { deferred = null; emit(); });
}

// Is the native Chrome/Android install prompt available right now?
export function canInstall() { return !!deferred; }

// Subscribe to availability changes (prompt arrived, or app got installed).
export function onInstallChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Fire the native install prompt. Returns 'accepted' | 'dismissed' | null.
export async function promptInstall() {
  if (!deferred) return null;
  deferred.prompt();
  let outcome = null;
  try { outcome = (await deferred.userChoice)?.outcome ?? null; } catch {}
  deferred = null;        // the event is single-use
  emit();
  return outcome;
}

// Already running as an installed app?
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

export function isIos() {
  if (typeof window === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
}

export function isIosSafari() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return isIos() && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}
